import React, { useState, useEffect } from 'react';
import { Clock, User, FileText, AlertCircle, CheckCircle, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { TIME_CONSTANTS } from '../lib/constants';
import { ScheduleItem, Task, Doctor, Staff, Room } from './SchedulerTypes';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => void;
  scheduleItem: ScheduleItem | null;
  existingTask: Task | null;
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
}

const TaskModal = ({
  isOpen,
  onClose,
  onSave,
  scheduleItem,
  existingTask,
  doctors,
  staff,
  rooms,
}: TaskModalProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [taskType, setTaskType] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [estimatedDuration, setEstimatedDuration] = useState<number>(TIME_CONSTANTS.DEFAULT_TASK_DURATION);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'pending' | 'in-progress' | 'completed' | 'cancelled'>('pending');

  // Use a ref to track if we've initialized the form for this task
  const initializedTaskId = React.useRef<string | null>(null);

  useEffect(() => {
    // Only initialize once when modal opens
    if (!isOpen) {
      initializedTaskId.current = null;
      return;
    }

    const taskId = existingTask?.id || 'new';
    if (initializedTaskId.current === taskId) {
      return; // Already initialized for this task
    }

    if (existingTask) {
      setTitle(existingTask.title);
      setDescription(existingTask.description);
      setTaskType(existingTask.taskType);
      setPriority(existingTask.priority);
      setEstimatedDuration(existingTask.estimatedDuration);
      setNotes(existingTask.notes || '');
      setStatus(existingTask.status);
    } else {
      // Reset form fields when creating a new task
      setTitle('');
      setDescription('');
      setTaskType('');
      setPriority('medium');
      setEstimatedDuration(TIME_CONSTANTS.DEFAULT_TASK_DURATION);
      setNotes('');
      setStatus('pending');
    }

    initializedTaskId.current = taskId;
  }, [existingTask, isOpen]);

  const handleSave = () => {
    if (!scheduleItem) return;

    const taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'> = {
      scheduleItemId: scheduleItem.id,
      title,
      description,
      taskType,
      priority,
      estimatedDuration,
      notes,
      status,
    };
    onSave(taskData);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>
            {existingTask ? 'Edit Task' : 'Assign Task'}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="title" className="text-left sm:text-right">
              Title
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full sm:col-span-3"
            />
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="description" className="text-left sm:text-right">
              Description
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full sm:col-span-3"
            />
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="taskType" className="text-left sm:text-right">
              Task Type
            </Label>
            <Input
              id="taskType"
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
              className="w-full sm:col-span-3"
            />
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="priority" className="text-left sm:text-right">
              Priority
            </Label>
            <Select value={priority} onValueChange={(value) => setPriority(value as 'low' | 'medium' | 'high' | 'urgent')}>
              <SelectTrigger className="w-full sm:col-span-3">
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="estimatedDuration" className="text-left sm:text-right">
              Estimated Duration (mins)
            </Label>
            <Input
              id="estimatedDuration"
              type="number"
              value={estimatedDuration.toString()}
              onChange={(e) => setEstimatedDuration(parseInt(e.target.value) || 0)}
              className="w-full sm:col-span-3"
            />
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="notes" className="text-left sm:text-right">
              Notes
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full sm:col-span-3"
            />
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="status" className="text-left sm:text-right">
              Status
            </Label>
            <Select value={status} onValueChange={(value) => setStatus(value as 'pending' | 'in-progress' | 'completed' | 'cancelled')}>
              <SelectTrigger className="w-full sm:col-span-3">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave}>
            {existingTask ? 'Update Task' : 'Save Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskModal;
